import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiInfo } from 'react-icons/fi'
import { FileItem } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface FilePropertiesModalProps {
  isOpen: boolean
  onClose: () => void
  file: FileItem
}

export default function FilePropertiesModal({ isOpen, onClose, file }: FilePropertiesModalProps) {
  const labelColor = useColorModeValue('gray.600', 'gray.400')
  const valueColor = useColorModeValue('gray.800', 'gray.200')
  const bgColor = useColorModeValue('gray.50', 'gray.700')
  const { t, formatFileSize, formatDateTime } = useAppTranslation('files')
  const { t: tCommon } = useAppTranslation('common')

  const getStatusBadge = (status: string) => {
    const colorScheme =
      {
        pending: 'yellow',
        processing: 'blue',
        completed: 'green',
        failed: 'red',
      }[status] || 'gray'

    return (
      <Badge colorScheme={colorScheme} variant="subtle">
        {t(`filePropertiesModal.status.${status}`) ||
          status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const PropertyRow = ({
    label,
    value,
    children,
  }: {
    label: string
    value?: string
    children?: React.ReactNode
  }) => (
    <HStack justify="space-between" align="start" w="full">
      <Text fontSize="sm" color={labelColor} fontWeight="medium" minW="120px">
        {label}:
      </Text>
      <Box textAlign="right" flex={1}>
        {children || (
          <Text fontSize="sm" color={valueColor} wordBreak="break-word">
            {value || 'N/A'}
          </Text>
        )}
      </Box>
    </HStack>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="brand.600"
        >
          <HStack spacing={2}>
            <FiInfo />
            <Text>{t('filePropertiesModal.title')}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* General Information */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                {t('filePropertiesModal.sections.general')}
              </Text>
              <Box bg={bgColor} p={3} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <PropertyRow
                    label={t('filePropertiesModal.labels.displayName')}
                    value={file.display_name || file.original_name}
                  />
                  <PropertyRow
                    label={t('filePropertiesModal.labels.originalName')}
                    value={file.original_name}
                  />
                  <PropertyRow
                    label={t('filePropertiesModal.labels.systemName')}
                    value={file.name}
                  />
                  <PropertyRow
                    label={t('filePropertiesModal.labels.fileType')}
                    value={file.mime_type}
                  />
                  <PropertyRow
                    label={t('filePropertiesModal.labels.fileSize')}
                    value={formatFileSize(file.file_size)}
                  />
                  {file.page_count && (
                    <PropertyRow
                      label={t('filePropertiesModal.labels.pageCount')}
                      value={file.page_count.toString()}
                    />
                  )}
                </VStack>
              </Box>
            </Box>

            {/* Processing Status */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                {t('filePropertiesModal.sections.processingStatus')}
              </Text>
              <Box bg={bgColor} p={3} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <PropertyRow label={t('filePropertiesModal.labels.ocrStatus')}>
                    {getStatusBadge(file.ocr_status)}
                  </PropertyRow>
                  <PropertyRow label={t('filePropertiesModal.labels.embeddingStatus')}>
                    {getStatusBadge(file.embedding_status)}
                  </PropertyRow>
                </VStack>
              </Box>
            </Box>

            {/* Timestamps */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                {t('filePropertiesModal.sections.timestamps')}
              </Text>
              <Box bg={bgColor} p={3} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <PropertyRow
                    label={t('filePropertiesModal.labels.created')}
                    value={formatDateTime(new Date(file.created_at))}
                  />
                  <PropertyRow
                    label={t('filePropertiesModal.labels.modified')}
                    value={formatDateTime(new Date(file.updated_at))}
                  />
                  {file.deleted_at && (
                    <PropertyRow
                      label={t('filePropertiesModal.labels.deleted')}
                      value={formatDateTime(new Date(file.deleted_at))}
                    />
                  )}
                </VStack>
              </Box>
            </Box>

            {/* Organization */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                {t('filePropertiesModal.sections.organization')}
              </Text>
              <Box bg={bgColor} p={3} borderRadius="md">
                <VStack spacing={3} align="stretch">
                  <PropertyRow
                    label={t('filePropertiesModal.labels.uploadedBy')}
                    value={file.uploader?.name || `User ${file.uploaded_by}`}
                  />
                </VStack>
              </Box>
            </Box>

            {/* Tags */}
            {Boolean(file.tags?.length) && (
              <Box>
                <Text fontSize="md" fontWeight="bold" mb={3} color={valueColor}>
                  {t('filePropertiesModal.sections.tags')}
                </Text>
                <Box bg={bgColor} p={3} borderRadius="md">
                  <HStack spacing={2} wrap="wrap">
                    {file.tags?.map(({ tag: name }, index) => (
                      <Badge key={index} colorScheme="blue" variant="subtle" borderRadius="sm">
                        {name}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.200"
          bg="gray.50"
          borderBottomRadius="xl"
        >
          <Button onClick={onClose} colorScheme="brand">
            {tCommon('buttons.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
